import { Fragment } from "react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Download } from "lucide-react";
import { fmtFecha, fmtFechaCorta, fmtMXN, r2 } from "@/lib/operativo";
import { AccionesMenu } from "@/components/shell/AccionesMenu";
import { TarjetaPeriodo, TarjetaTotal } from "@/components/shell/TarjetasPeriodo";
import { useOperativo } from "@/lib/operativo-store";

export function TablaReal() {
  const { real } = useOperativo();
  const b = real.backlog;
  const totalCuotaEsperada = r2(real.filas.reduce((a, f) => a + f.cuotaEsperada, 0));
  const totalAdeudoPeriodo = r2(real.filas.reduce((a, f) => a + f.adeudoPeriodo, 0));
  /** Separa el capital aplicado entre la porción programada del período y el abono extra. */
  const capCuota = (aplCapital: number, capitalProg: number) =>
    r2(Math.min(aplCapital, capitalProg));
  const capExtra = (aplCapital: number, capitalProg: number) =>
    r2(Math.max(0, aplCapital - capitalProg));
  const totalCapCuota = r2(real.filas.reduce((a, f) => a + capCuota(f.aplCapital, f.capital), 0));
  const totalCapExtra = r2(real.filas.reduce((a, f) => a + capExtra(f.aplCapital, f.capital), 0));

  const exportarCSV = () => {
    const head = [
      "Periodo",
      "Vencimiento",
      "Cuota esperada",
      "Tramos",
      "Fecha real de ultimo pago",
      "Monto pagado",
      "Dias atraso",
      "Devengado Mora + IVA",
      "Devengado Pena convencional",
      "Devengado IVA pena",
      "Pago IVA pena",
      "Pago Pena convencional",
      "Pago IVA moratorio",
      "Pago Moratorio",
      "Pago IVA ordinario",
      "Pago Ordinario",
      "Pago Capital",
      "Abono extra a capital",
      "Saldo final",
      "Estatus",
      "Saldo pendiente",
    ];
    const filas = real.filas.map((f) =>
      [
        f.periodo,
        fmtFecha(f.fechaProg),
        f.cuotaEsperada,
        f.tramos.length,
        fmtFechaCorta(f.fechaUltimoPago),
        f.montoPagado,
        f.diasAtraso,
        r2(f.mora + f.ivaMora),
        f.pena,
        f.ivaPena,
        f.aplIvaPena,
        f.aplPena,
        f.aplIvaMora,
        f.aplMora,
        f.aplIvaInteres,
        f.aplInteres,
        capCuota(f.aplCapital, f.capital),
        capExtra(f.aplCapital, f.capital),
        f.saldoFinalReal,
        f.estatusPeriodo,
        f.adeudoPeriodo,
      ].join(","),
    );
    const total = [
      "TOTAL",
      "",
      totalCuotaEsperada,
      "",
      "",
      real.pagado.total,
      "",
      r2(real.moraVigente + real.ivaMoraVigente),
      r2(real.filas.reduce((a, f) => a + f.pena, 0)),
      r2(real.filas.reduce((a, f) => a + f.ivaPena, 0)),
      real.pagado.ivaPena,
      real.pagado.pena,
      real.pagado.ivaMora,
      real.pagado.mora,
      real.pagado.ivaInteres,
      real.pagado.interes,
      totalCapCuota,
      totalCapExtra,
      real.saldoCapital,
      "",
      totalAdeudoPeriodo,
    ].join(",");
    const csv = [head.join(","), ...filas, total].join("\n");
    const url = URL.createObjectURL(
      new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" }),
    );
    const a = document.createElement("a");
    a.href = url;
    a.download = "control-de-pagos.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Card className="print-block overflow-hidden border-navy/15 p-0 shadow-sm">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b bg-surface px-5 py-4">
        <h3 className="text-sm font-semibold uppercase tracking-wide text-navy">
          Aplicación real de pagos (prelación RN-06)
        </h3>
        <div className="flex flex-wrap items-center gap-2">
          <Badge variant="outline" className="num">
            Sobrepago a favor: {fmtMXN(real.sobrepago)}
          </Badge>
          <Badge variant="secondary" className="num">
            Saldo de capital: {fmtMXN(real.saldoCapital)}
          </Badge>
          <AccionesMenu
            acciones={[
              {
                label: "Exportar CSV",
                icono: <Download className="h-4 w-4" />,
                onClick: exportarCSV,
              },
            ]}
          />
        </div>
      </div>
      <div className="solo-movil space-y-3 p-4 lg:hidden">
        {real.filas.map((f) => (
          <TarjetaPeriodo
            key={f.periodo}
            titulo={
              <>
                Período {f.periodo}
                {f.atraso ? (
                  <Badge variant="destructive" className="text-[10px]">
                    En mora
                  </Badge>
                ) : f.liquidado ? (
                  <Badge className="bg-emerald text-[10px] text-primary-foreground">Cubierto</Badge>
                ) : f.montoPagado > 0 ? (
                  <Badge variant="secondary" className="text-[10px]">
                    Pago parcial
                  </Badge>
                ) : (
                  <Badge variant="secondary" className="text-[10px]">
                    Pendiente
                  </Badge>
                )}
              </>
            }
            fecha={`Vence ${fmtFecha(f.fechaProg)}`}
            destacado={{ l: "Monto pagado", v: fmtMXN(f.montoPagado) }}
            resumen={[
              {
                l: f.reamortizada ? "Cuota esperada (re-amortizada)" : "Cuota esperada",
                v: fmtMXN(f.cuotaEsperada),
                tono: "chart3",
              },
              {
                l: "Días de atraso (a la fecha de consulta)",
                v: f.diasAtraso,
                tono: f.diasAtraso > 0 ? "destructive" : "navy",
              },
              {
                l: "Devengado Mora + IVA",
                v: fmtMXN(f.mora + f.ivaMora),
                tono: f.mora > 0 ? "destructive" : "navy",
              },
              {
                l: "Saldo pendiente",
                v: fmtMXN(f.adeudoPeriodo),
                tono: f.liquidado
                  ? "emerald"
                  : f.estatusPeriodo === "Pago parcial"
                    ? "chart3"
                    : f.estatusPeriodo === "En mora"
                      ? "destructive"
                      : "navy",
              },
            ]}
            detalle={[
              {
                l: "Segmentos de mora",
                v:
                  f.segmentos.length === 0
                    ? "Sin devengo"
                    : f.segmentos
                        .map((s) => `${s.dias}d ${s.motivo === "abierto" ? "(abierto)" : ""}`)
                        .join(" · "),
                tono: "muted",
              },
              {
                l: "Mora vigente (segmento abierto)",
                v: fmtMXN(f.moraVigente + f.ivaMoraVigente),
                tono: "destructive",
              },
              {
                l: "Fecha real de último pago",
                v: fmtFechaCorta(f.fechaUltimoPago),
                tono: "muted",
              },
              { l: "Devengado Pena convencional", v: fmtMXN(f.pena), tono: "destructive" },
              { l: "Devengado IVA pena", v: fmtMXN(f.ivaPena), tono: "muted" },
              { l: "Pago IVA pena", v: fmtMXN(f.aplIvaPena), tono: "muted" },
              { l: "Pago Pena convencional", v: fmtMXN(f.aplPena), tono: "destructive" },
              { l: "Pago IVA moratorio", v: fmtMXN(f.aplIvaMora), tono: "muted" },
              { l: "Pago Moratorio", v: fmtMXN(f.aplMora), tono: "destructive" },
              { l: "Pago IVA ordinario", v: fmtMXN(f.aplIvaInteres), tono: "muted" },
              { l: "Pago Ordinario", v: fmtMXN(f.aplInteres), tono: "chart3" },
              {
                l: "Pago Capital",
                v: fmtMXN(capCuota(f.aplCapital, f.capital)),
                tono: "emerald",
              },
              {
                l: "Abono extra a Capital",
                v: fmtMXN(capExtra(f.aplCapital, f.capital)),
                tono: "emerald",
              },
              {
                l: "Tramos de pago",
                v: f.tramos.length === 0 ? "Sin pago" : `${f.tramos.length} tramo(s)`,
                tono: "muted",
              },
            ]}
          />
        ))}
        <TarjetaTotal
          titulo="TOTAL APLICADO"
          datos={[
            { l: "Cuota esperada", v: fmtMXN(totalCuotaEsperada), tono: "chart3" },
            { l: "Total pagado", v: fmtMXN(real.pagado.total) },
            { l: "Capital (cuota del mes)", v: fmtMXN(totalCapCuota), tono: "emerald" },
            { l: "Abono extra a Capital", v: fmtMXN(totalCapExtra), tono: "emerald" },
            {
              l: "Interés + IVA",
              v: fmtMXN(real.pagado.interes + real.pagado.ivaInteres),
              tono: "chart3",
            },
            {
              l: "Mora + IVA",
              v: fmtMXN(real.pagado.mora + real.pagado.ivaMora),
              tono: "destructive",
            },
            {
              l: "Pena + IVA",
              v: fmtMXN(real.pagado.pena + real.pagado.ivaPena),
              tono: "destructive",
            },
            { l: "Saldo de capital", v: fmtMXN(real.saldoCapital) },
            {
              l: "Adeudo total a la fecha de consulta",
              v: fmtMXN(totalAdeudoPeriodo),
              tono: totalAdeudoPeriodo > 0 ? "destructive" : "emerald",
            },
          ]}
        />
      </div>

      <div className="solo-desktop hidden overflow-x-auto lg:block">
        <table className="w-full min-w-[2200px] text-sm">
          <thead>
            <tr className="bg-navy text-primary-foreground">
              <th rowSpan={2} className="px-3 py-2.5 text-left font-medium">
                Per.
              </th>
              <th rowSpan={2} className="px-3 py-2.5 text-left font-medium">
                Vencimiento
              </th>
              <th rowSpan={2} className="px-3 py-2.5 text-right font-medium">
                Cuota esperada
              </th>
              <th rowSpan={2} className="px-3 py-2.5 text-left font-medium">
                Pago / ref.
              </th>
              <th rowSpan={2} className="px-3 py-2.5 text-left font-medium">
                Fecha real de último pago
              </th>
              <th rowSpan={2} className="px-3 py-2.5 text-right font-medium">
                Monto pagado
              </th>
              <th rowSpan={2} className="px-3 py-2.5 text-right font-medium">
                Días atraso
              </th>
              <th
                colSpan={3}
                className="border-x border-primary-foreground/25 px-3 py-2 text-center font-semibold"
              >
                Devengado
              </th>
              <th
                colSpan={8}
                className="border-r border-primary-foreground/25 px-3 py-2 text-center font-semibold"
              >
                Aplicado al pago
              </th>
              <th rowSpan={2} className="px-3 py-2.5 text-right font-medium">
                Saldo final
              </th>
              <th rowSpan={2} className="px-3 py-2.5 text-left font-medium">
                Estatus
              </th>
              <th rowSpan={2} className="px-3 py-2.5 text-right font-medium">
                Saldo pendiente
              </th>
            </tr>
            <tr className="bg-navy text-primary-foreground">
              <th className="border-l border-primary-foreground/25 px-3 py-2 text-right font-medium">
                Devengado Mora + IVA
              </th>
              <th className="px-3 py-2 text-right font-medium">Devengado Pena convencional</th>
              <th className="border-r border-primary-foreground/25 px-3 py-2 text-right font-medium">
                Devengado IVA pena
              </th>
              <th className="px-3 py-2 text-right font-medium">Pago IVA pena</th>
              <th className="px-3 py-2 text-right font-medium">Pago Pena convencional</th>
              <th className="px-3 py-2 text-right font-medium">Pago IVA moratorio</th>
              <th className="px-3 py-2 text-right font-medium">Pago Moratorio</th>
              <th className="px-3 py-2 text-right font-medium">Pago IVA ordinario</th>
              <th className="px-3 py-2 text-right font-medium">Pago Ordinario</th>
              <th className="px-3 py-2 text-right font-medium">Pago Capital</th>
              <th className="border-r border-primary-foreground/25 px-3 py-2 text-right font-medium">
                Abono extra a Capital
              </th>
            </tr>
          </thead>
          <tbody>
            {real.filas.map((f) => (
              <Fragment key={f.periodo}>
                <tr
                  className={`border-b font-medium ${f.atraso ? "bg-destructive/10" : "bg-navy-soft/50"}`}
                >
                  <td className="px-3 py-2 num">{f.periodo}</td>
                  <td className="px-3 py-2 num text-muted-foreground">{fmtFecha(f.fechaProg)}</td>
                  <td className="px-3 py-2 text-right num">
                    {fmtMXN(f.cuotaEsperada)}
                    {f.reamortizada && (
                      <span
                        className="ml-1 text-xs text-muted-foreground"
                        title="Cuota recalculada por un abono extra a capital"
                      >
                        re-amortizada
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-muted-foreground">
                    {f.tramos.length === 0 ? "Sin pago" : `${f.tramos.length} tramo(s)`}
                  </td>
                  <td className="px-3 py-2 num text-muted-foreground">
                    {fmtFechaCorta(f.fechaUltimoPago)}
                  </td>
                  <td className="px-3 py-2 text-right num">{fmtMXN(f.montoPagado)}</td>
                  <td
                    className={`px-3 py-2 text-right num ${f.diasAtraso > 0 ? "text-destructive" : ""}`}
                  >
                    {f.diasAtraso}
                  </td>
                  <td
                    className={`px-3 py-2 text-right num ${f.mora > 0 ? "text-destructive" : ""}`}
                  >
                    {fmtMXN(f.mora + f.ivaMora)}
                  </td>
                  <td className="px-3 py-2 text-right num">{fmtMXN(f.pena)}</td>
                  <td className="px-3 py-2 text-right num">{fmtMXN(f.ivaPena)}</td>
                  <td className="px-3 py-2 text-right num">{fmtMXN(f.aplIvaPena)}</td>
                  <td className="px-3 py-2 text-right num">{fmtMXN(f.aplPena)}</td>
                  <td className="px-3 py-2 text-right num">{fmtMXN(f.aplIvaMora)}</td>
                  <td className="px-3 py-2 text-right num text-destructive">{fmtMXN(f.aplMora)}</td>
                  <td className="px-3 py-2 text-right num">{fmtMXN(f.aplIvaInteres)}</td>
                  <td className="px-3 py-2 text-right num text-chart-3">{fmtMXN(f.aplInteres)}</td>
                  <td className="px-3 py-2 text-right num text-emerald">
                    {fmtMXN(capCuota(f.aplCapital, f.capital))}
                  </td>
                  <td className="px-3 py-2 text-right num text-emerald">
                    {fmtMXN(capExtra(f.aplCapital, f.capital))}
                  </td>
                  <td className="px-3 py-2 text-right num">{fmtMXN(f.saldoFinalReal)}</td>
                  <td className="px-3 py-2">
                    {f.atraso ? (
                      <Badge variant="destructive">En mora</Badge>
                    ) : f.liquidado ? (
                      <Badge className="bg-emerald text-primary-foreground">Cubierto</Badge>
                    ) : f.montoPagado > 0 ? (
                      <Badge variant="secondary">Pago parcial</Badge>
                    ) : (
                      <Badge variant="secondary">Pendiente</Badge>
                    )}
                  </td>
                  <td
                    className={`px-3 py-2 text-right num font-semibold ${f.liquidado ? "text-emerald" : f.estatusPeriodo === "Pago parcial" ? "text-chart-3" : f.estatusPeriodo === "En mora" ? "text-destructive" : "text-navy"}`}
                  >
                    {fmtMXN(f.adeudoPeriodo)}
                  </td>
                </tr>
                {f.tramos.length > 1 &&
                  f.tramos.map((t, i) => (
                    <tr
                      key={`${f.periodo}-${i}`}
                      className="border-b text-xs text-muted-foreground"
                    >
                      <td className="px-3 py-1.5" />
                      <td className="px-3 py-1.5 num">{fmtFecha(t.fecha)}</td>
                      <td className="px-3 py-1.5" />
                      <td className="px-3 py-1.5">{t.referencia || `Tramo ${i + 1}`}</td>
                      <td className="px-3 py-1.5 num">{fmtFechaCorta(t.fecha)}</td>
                      <td className="px-3 py-1.5 text-right num">{fmtMXN(t.monto)}</td>
                      <td className="px-3 py-1.5 text-right num">{t.diasAtraso}</td>
                      <td className="px-3 py-1.5 text-right num">{fmtMXN(t.mora + t.ivaMora)}</td>
                      <td className="px-3 py-1.5" colSpan={2} />
                      <td className="px-3 py-1.5 text-right num">{fmtMXN(t.aplIvaPena)}</td>
                      <td className="px-3 py-1.5 text-right num">{fmtMXN(t.aplPena)}</td>
                      <td className="px-3 py-1.5 text-right num">{fmtMXN(t.aplIvaMora)}</td>
                      <td className="px-3 py-1.5 text-right num">{fmtMXN(t.aplMora)}</td>
                      <td className="px-3 py-1.5 text-right num">{fmtMXN(t.aplIvaInteres)}</td>
                      <td className="px-3 py-1.5 text-right num">{fmtMXN(t.aplInteres)}</td>
                      <td className="px-3 py-1.5 text-right num">
                        {fmtMXN(capCuota(t.aplCapital, f.capital))}
                      </td>
                      <td className="px-3 py-1.5 text-right num">
                        {fmtMXN(capExtra(t.aplCapital, f.capital))}
                      </td>
                      <td className="px-3 py-1.5" colSpan={3} />
                    </tr>
                  ))}
              </Fragment>
            ))}
          </tbody>
          <tfoot>
            <tr className="bg-navy-soft font-semibold text-navy">
              <td className="px-3 py-2.5" colSpan={2}>
                TOTAL APLICADO
              </td>
              <td className="px-3 py-2.5 text-right num">{fmtMXN(totalCuotaEsperada)}</td>
              <td className="px-3 py-2.5" colSpan={2} />
              <td className="px-3 py-2.5 text-right num">{fmtMXN(real.pagado.total)}</td>
              <td className="px-3 py-2.5" />
              <td className="px-3 py-2.5 text-right num">
                {fmtMXN(real.moraVigente + real.ivaMoraVigente)}
              </td>
              <td className="px-3 py-2.5 text-right num">
                {fmtMXN(real.filas.reduce((a, f) => a + f.pena, 0))}
              </td>
              <td className="px-3 py-2.5 text-right num">
                {fmtMXN(real.filas.reduce((a, f) => a + f.ivaPena, 0))}
              </td>
              <td className="px-3 py-2.5 text-right num">{fmtMXN(real.pagado.ivaPena)}</td>
              <td className="px-3 py-2.5 text-right num">{fmtMXN(real.pagado.pena)}</td>
              <td className="px-3 py-2.5 text-right num">{fmtMXN(real.pagado.ivaMora)}</td>
              <td className="px-3 py-2.5 text-right num">{fmtMXN(real.pagado.mora)}</td>
              <td className="px-3 py-2.5 text-right num">{fmtMXN(real.pagado.ivaInteres)}</td>
              <td className="px-3 py-2.5 text-right num">{fmtMXN(real.pagado.interes)}</td>
              <td className="px-3 py-2.5 text-right num">{fmtMXN(totalCapCuota)}</td>
              <td className="px-3 py-2.5 text-right num">{fmtMXN(totalCapExtra)}</td>
              <td className="px-3 py-2.5 text-right num">{fmtMXN(real.saldoCapital)}</td>
              <td className="px-3 py-2.5" />
              <td className="px-3 py-2.5 text-right num text-destructive">
                <span className="block text-[10px] font-medium uppercase">
                  Adeudo total a la fecha de consulta
                </span>
                {fmtMXN(totalAdeudoPeriodo)}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
      <div className="grid grid-cols-2 gap-3 border-t bg-surface px-5 py-4 sm:grid-cols-3 lg:grid-cols-6">
        {[
          { l: "IVA pena por cobrar", v: b.ivaPena },
          { l: "Pena por cobrar", v: b.pena },
          { l: "IVA mora por cobrar", v: b.ivaMora },
          { l: "Mora por cobrar", v: b.mora },
          { l: "IVA interés por cobrar", v: b.ivaInteres },
          { l: "Interés por cobrar", v: b.interes },
        ].map((x) => (
          <div key={x.l}>
            <p className="text-xs text-muted-foreground">{x.l}</p>
            <p
              className={`num text-sm font-semibold ${x.v > 0 ? "text-destructive" : "text-navy"}`}
            >
              {fmtMXN(x.v)}
            </p>
          </div>
        ))}
      </div>
    </Card>
  );
}
